import React, { useState, useEffect } from 'react'
import { Edit2, Loader2, Building2 } from 'lucide-react'
import { Drawer } from '../ui'
import { useToast } from '../../contexts/ToastContext'
import useContactStore from '../../stores/contactStore'
import useContactRoleStore from '../../stores/contactRoleStore'
import useGlobalCustomerStore from '../../stores/globalCustomerStore'
import { Contact, ContactStatus } from '../../types/contact'
import { isValidEmail, isValidPhone } from '../../utils/validation'

interface EditContactDrawerProps {
  isOpen: boolean
  onClose: () => void
  contact: Contact | null
  onContactUpdated?: () => void
}

const EditContactDrawer: React.FC<EditContactDrawerProps> = ({
  isOpen,
  onClose,
  contact,
  onContactUpdated
}) => {
  const { showSuccess, showError } = useToast()
  const { updateContact, loadContacts } = useContactStore()
  const { roles, loadRoles } = useContactRoleStore()
  const { selectedCustomer } = useGlobalCustomerStore()

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [roleId, setRoleId] = useState('')
  const [jobTitle, setJobTitle] = useState('')
  const [email, setEmail] = useState('')
  const [phone, setPhone] = useState('')
  const [isPrimary, setIsPrimary] = useState(false)
  const [status, setStatus] = useState<ContactStatus>('Active')
  const [isUpdating, setIsUpdating] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [phoneError, setPhoneError] = useState('')

  // Load roles when drawer opens
  useEffect(() => {
    if (isOpen) {
      loadRoles(true) // Only load active roles
    }
  }, [isOpen, loadRoles])

  // Populate form when contact changes
  useEffect(() => {
    if (contact) {
      setFirstName(contact.first_name)
      setLastName(contact.last_name)
      setRoleId(contact.role_id)
      setJobTitle(contact.job_title || '')
      setEmail(contact.email || '')
      setPhone(contact.phone || '')
      setIsPrimary(contact.is_primary)
      setStatus(contact.status)
    }
  }, [contact])

  const resetForm = () => {
    setFirstName('')
    setLastName('')
    setRoleId('')
    setJobTitle('')
    setEmail('')
    setPhone('')
    setIsPrimary(false)
    setStatus('Active')
    setEmailError('')
    setPhoneError('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!contact) return

    if (!selectedCustomer?.id) {
      showError('Error', 'Please select a customer first')
      return
    }

    if (!firstName.trim()) {
      showError('Validation Error', 'First name is required')
      return
    }

    if (firstName.length > 255) {
      showError('Validation Error', 'First name must be 255 characters or less')
      return
    }

    if (!lastName.trim()) {
      showError('Validation Error', 'Last name is required')
      return
    }

    if (lastName.length > 255) {
      showError('Validation Error', 'Last name must be 255 characters or less')
      return
    }

    if (!roleId) {
      showError('Validation Error', 'Role is required')
      return
    }

    if (email && !isValidEmail(email)) {
      showError('Validation Error', 'Please enter a valid email address')
      return
    }

    if (phone && !isValidPhone(phone)) {
      showError('Validation Error', 'Please enter a valid phone number')
      return
    }

    setIsUpdating(true)

    try {
      const updated = await updateContact(
        contact.id,
        String(selectedCustomer.id),
        {
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          role_id: roleId,
          job_title: jobTitle.trim() || undefined,
          email: email.trim() || undefined,
          phone: phone.trim() || undefined,
          is_primary: isPrimary,
          status
        }
      )

      showSuccess('Contact Updated', `"${updated.first_name} ${updated.last_name}" has been updated successfully`)

      // Reload contacts list to get the full contact data with nested objects
      await loadContacts(String(selectedCustomer.id))

      // Call the callback if provided (for PartnerDetailDrawer)
      onContactUpdated?.()

      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showError(
        'Failed to update contact',
        errorMessage.includes('not found')
          ? 'Contact not found'
          : 'Please try again later'
      )
    } finally {
      setIsUpdating(false)
    }
  }

  const validateEmail = (emailValue: string) => {
    if (emailValue && !isValidEmail(emailValue)) {
      setEmailError('Please enter a valid email address')
    } else {
      setEmailError('')
    }
  }

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setEmail(value)
    // Clear error when user starts typing again
    if (emailError && (!value || isValidEmail(value))) {
      setEmailError('')
    }
  }

  const validatePhone = (phoneValue: string) => {
    if (phoneValue && !isValidPhone(phoneValue)) {
      setPhoneError('Please enter a valid phone number')
    } else {
      setPhoneError('')
    }
  }

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setPhone(value)
    // Clear error when user starts typing again
    if (phoneError && (!value || isValidPhone(value))) {
      setPhoneError('')
    }
  }

  // Get active roles (include current role even if inactive)
  const activeRoles = roles.filter((r) => r.is_active || r.id === contact?.role_id)

  return (
    <Drawer
      isOpen={isOpen}
      onClose={handleClose}
      title="Edit Contact"
      description="Update contact information"
      icon={<Edit2 className="h-5 w-5" />}
      width="md"
    >
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Partner */}
        <div className="flex items-center gap-2 text-xs text-gray-600 mb-2">
          <Building2 className="h-4 w-4 text-gray-400" />
          <span className="font-semibold">{contact?.partners.name}</span>
        </div>

        {/* First Name + Last Name Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-first-name" className="block text-xs font-medium text-gray-700 mb-1">
              First Name <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-first-name"
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="John"
              maxLength={255}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
          <div>
            <label htmlFor="edit-last-name" className="block text-xs font-medium text-gray-700 mb-1">
              Last Name <span className="text-red-500">*</span>
            </label>
            <input
              id="edit-last-name"
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Doe"
              maxLength={255}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Role + Job Title Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-role" className="block text-xs font-medium text-gray-700 mb-1">
              Role <span className="text-red-500">*</span>
            </label>
            <select
              id="edit-role"
              value={roleId}
              onChange={(e) => setRoleId(e.target.value)}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="">Select role</option>
              {activeRoles.map((role) => (
                <option key={role.id} value={role.id}>
                  {role.name}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="edit-job-title" className="block text-xs font-medium text-gray-700 mb-1">
              Job Title
            </label>
            <input
              id="edit-job-title"
              type="text"
              value={jobTitle}
              onChange={(e) => setJobTitle(e.target.value)}
              placeholder="Marketing Manager"
              maxLength={255}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            />
          </div>
        </div>

        {/* Email + Phone Row */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor="edit-email" className="block text-xs font-medium text-gray-700 mb-1">
              Email
            </label>
            <input
              id="edit-email"
              type="email"
              value={email}
              onChange={handleEmailChange}
              onBlur={() => validateEmail(email)}
              placeholder="john@example.com"
              maxLength={255}
              disabled={isUpdating}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${emailError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {emailError && (
              <p className="text-xs text-red-500 mt-0.5">{emailError}</p>
            )}
          </div>
          <div>
            <label htmlFor="edit-phone" className="block text-xs font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              id="edit-phone"
              type="text"
              value={phone}
              onChange={handlePhoneChange}
              onBlur={() => validatePhone(phone)}
              placeholder="+1 (555) 123-4567"
              maxLength={50}
              disabled={isUpdating}
              className={`w-full px-2 py-1.5 text-xs border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed ${phoneError ? 'border-red-500' : 'border-gray-300'}`}
            />
            {phoneError && (
              <p className="text-xs text-red-500 mt-0.5">{phoneError}</p>
            )}
          </div>
        </div>

        {/* Primary + Status Row */}
        <div className="grid grid-cols-2 gap-3 items-end">
          <div className="flex items-center gap-2">
            <input
              id="edit-is-primary"
              type="checkbox"
              checked={isPrimary}
              onChange={(e) => setIsPrimary(e.target.checked)}
              disabled={isUpdating}
              className="h-3.5 w-3.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label htmlFor="edit-is-primary" className="text-xs text-gray-700">
              Primary contact
            </label>
          </div>
          <div>
            <label htmlFor="edit-status" className="block text-xs font-medium text-gray-700 mb-1">
              Status
            </label>
            <select
              id="edit-status"
              value={status}
              onChange={(e) => setStatus(e.target.value as ContactStatus)}
              disabled={isUpdating}
              className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white disabled:bg-gray-100 disabled:text-gray-500 disabled:cursor-not-allowed"
            >
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center space-x-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={isUpdating}
            className="flex-1 px-3 py-1.5 bg-white border border-gray-300 text-gray-700 text-xs font-medium rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={isUpdating || !firstName.trim() || !lastName.trim() || !roleId || !!emailError || !!phoneError}
            className="flex-1 inline-flex items-center justify-center px-3 py-1.5 bg-gradient-to-r from-blue-600 to-blue-700 text-white text-xs font-medium rounded-lg hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 shadow-sm hover:shadow-md disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Edit2 className="h-3.5 w-3.5 mr-1.5" />
                Update Contact
              </>
            )}
          </button>
        </div>
      </form>
    </Drawer>
  )
}

export default EditContactDrawer
